import { AuthGuard } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Button,
    EmptyState,
    Icon,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarTime, TbRefresh } from "react-icons/tb";

import { PageActions } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { PERMISSIONS } from "@/constants/permissions";
import { EventRulesTable } from "@/pages/events/$id/rules/components/event-rules-table";
import { useEventType } from "@/queries/event-type.queries";
import { useRegenerateEventTypeSlots } from "@/queries/slot.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";

export const Route = createFileRoute(APP_ROUTES_FULL.EVENT_RULES)({
    component: ManagementEventRules,
});

function ManagementEventRules() {
    const { id: eventTypeId } = Route.useParams();
    const { t } = useTranslation();
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const regenerateSlotsMutation = useRegenerateEventTypeSlots();
    const { data: eventType } = useEventType(eventTypeId);

    if (!eventTypeId) {
        return (
            <AdminSurface className="p-8">
                <EmptyState
                    variant="simple"
                    icon={<TbCalendarTime size={40} className="text-muted-foreground" />}
                    title="no_events_found"
                />
            </AdminSurface>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <PageActions>
                <AuthGuard required={[PERMISSIONS.CALL_SVC_ADMIN_BASE]}>
                    <Button
                        className="bg-amber-400 text-amber-950 hover:bg-amber-500 dark:bg-amber-500 dark:hover:bg-amber-400"
                        disabled={regenerateSlotsMutation.isPending}
                        onClick={() => setRegenerateDialogOpen(true)}
                    >
                        <Icon icon={TbRefresh} />
                        {regenerateSlotsMutation.isPending ? t("loading") : t("regenerate_slots")}
                    </Button>
                </AuthGuard>
            </PageActions>

            <div className="flex-1 overflow-auto">
                <EventRulesTable eventTypeId={eventTypeId} />
            </div>

            <AlertDialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("regenerate_slots")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("regenerate_slots_confirmation", { title: eventType?.title ?? "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => regenerateSlotsMutation.mutate(eventTypeId)}>
                            {t("regenerate")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
